import React, { useContext, useState } from 'react';

// components
import ToggleInput from '../../../components/inputs/toggleInput';
import LoadingSpinner from '../../../components/nav/LoadingSpinner';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

// context
import { EditorContext } from '../../../context/EditorContext';

import useFormulaUpdateBaseMutation from '../../../hooks/formulas/useFormulaUpdateBaseMutation';
// hooks
import useIsProviderFrequencyLimitOverrideAllowed from '../../../hooks/formulas/useIsProviderFrequencyLimitOverrideAllowed';

interface FrequencyProviderSettingsProps {
    overrideProviderFrequencyLimits: boolean;
    isDisabled?: boolean;
}

const FrequencyProviderSettings: React.FC<
    React.PropsWithChildren<FrequencyProviderSettingsProps>
> = ({ overrideProviderFrequencyLimits, isDisabled }) => {
    const [isDefaultFrequencyOverride, setIsDefaultFrequencyOverride] =
        useState<boolean>(overrideProviderFrequencyLimits);

    const { editorDetails } = useContext(EditorContext);
    const { formula } = editorDetails;

    const { isLoading, isFrequencyLimitAllowed } =
        useIsProviderFrequencyLimitOverrideAllowed();

    const updateFormulaMutation = useFormulaUpdateBaseMutation(
        formula?.id as number,
    );

    const onDefaultFrequencyChanged = () => {
        setIsDefaultFrequencyOverride((state) => !state);
        updateFormulaMutation.mutate(
            {
                id: formula?.id as number,
                override_provider_frequency_limits: !isDefaultFrequencyOverride,
            },
            {
                onError: () => {
                    setIsDefaultFrequencyOverride(
                        overrideProviderFrequencyLimits,
                    );
                },
            },
        );
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isFrequencyLimitAllowed) {
        return null;
    }

    return (
        <DefaultFrequency>
            <TitleDescription>
                <Title>Provider frequency limits</Title>
                <Description>
                    Aampe respects delivery provider frequency limits. Expand
                    the reach by overriding delivery frequency limits. Aampe may
                    send a message for individual users, if it determines that
                    they will respond to this message.
                </Description>
            </TitleDescription>
            <Toggle>
                <Description>Override delivery frequency limits</Description>
                <ToggleInput
                    isActive={isDefaultFrequencyOverride}
                    toggleIsActive={onDefaultFrequencyChanged}
                    isLoading={false}
                    isError={false}
                    disabled={isDisabled}
                />
            </Toggle>
        </DefaultFrequency>
    );
};

export default FrequencyProviderSettings;

const DefaultFrequency = styled.div({
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing(3),
    fontSize: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
});

const TitleDescription = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
});

const Title = styled.div({
    lineHeight: '28px',
    fontWeight: 500,
});

const Description = styled.div({
    lineHeight: '24px',
    color: aampeTheme.colors.neutral09,
    fontWeight: 400,
});

const Toggle = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(3),
    alignItems: 'center',
});
